import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type{ RootState } from '../../../../app/store';
import {
  loadNotifications,
  markAsRead,
  markAllAsRead,
} from '../../../../app/store/slices/notificationSlice';
import type { Notification } from '../../../../types/notification';
import { Check, X } from 'lucide-react';
import { useNotificationStore } from '../../../../app/store/useNotificationStore';
import { useGetShipmentDetailsQuery } from '../../../../api/senderApi2';
import { useAcceptOfferRequestMutation, useRejectOfferRequestMutation } from '../../../../api/senderApi3';
import { useAcceptOfferRequestMutation as useTravellerAcceptOfferRequestMutation, useRejectOfferRequestMutation as useTravellerRejectOfferRequestMutation, useGetItineraryDetailsQuery } from '../../../../api/travellerApi3';
import { useMarkNotificationAsRead } from '../../../../api/NotificationApi';
import { getNotificationTitle, getNotificationMessage } from '../../../utils/notificationMessages';
import { colors } from '../../../styles/theme/colors';
import { useAuth0 } from '@auth0/auth0-react';
import boxIcon from '../../../../assets/box (1).svg';
import { useUserStore } from '../../../../app/store/useUserStore';
import { normalizeRole } from '../../../../types/user';
import { isTravellerRole } from '../../../utils/roleUtils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DeclineOfferDialog } from '../Dialog/DeclineOfferDialog';
import { KycVerificationDialog } from '../Dialog/KycVerificationDialog';
import { useKycVerification } from '../../../hooks/useKycVerification';
import { notificationStorage } from '../../../utils/notificationStorage';

const CATEGORY_MISMATCH_WARNING =
  "The shipment includes items outside your current carrying preferences. You can review the details below and proceed only if you're comfortable carrying these items.";

interface ShipmentOfferMismatchWarningProps {
  shipmentId: string;
  itineraryId: string;
  notificationId: string;
  onWeightAvailabilityChange?: (notificationId: string, isWeightAvailable: boolean) => void;
}

const ShipmentOfferMismatchWarning: React.FC<ShipmentOfferMismatchWarningProps> = ({ shipmentId, itineraryId, notificationId, onWeightAvailabilityChange }) => {
  const { data: shipmentData } = useGetShipmentDetailsQuery(shipmentId, { staleTime: 60000 });
  const { data: itineraryData } = useGetItineraryDetailsQuery(itineraryId, { staleTime: 60000 });

  const hasMismatch = React.useMemo(() => {
    const shipmentItems: { item_category_id?: string }[] = shipmentData?.data?.items ?? [];
    const itineraryCategories: { category_id?: string }[] = itineraryData?.categories ?? itineraryData?.data?.categories ?? [];

    if (!shipmentItems.length || !itineraryCategories.length) return false;

    const itineraryCategoryIds = new Set(itineraryCategories.map((c: { category_id?: string }) => c.category_id));
    return shipmentItems.some((item: { item_category_id?: string }) => item.item_category_id && !itineraryCategoryIds.has(item.item_category_id));
  }, [shipmentData, itineraryData]);

  React.useEffect(() => {
    if (!hasMismatch) return;
    const stored = notificationStorage.getNotifications();
    const notif = stored.find((n) => n.id === notificationId);
    if (notif && !notif.data?.hasCategoryMismatch) {
      notif.data = { ...notif.data, hasCategoryMismatch: true };
      const updated = stored.map((n) => (n.id === notificationId ? notif : n));
      localStorage.setItem('skyporter_notifications', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('notification-state-changed'));
    }
  }, [hasMismatch, notificationId]);

  React.useEffect(() => {
    if (!onWeightAvailabilityChange || !shipmentData) return;
    onWeightAvailabilityChange(notificationId, !!shipmentData?.data?.total_weight);
  }, [shipmentData, notificationId, onWeightAvailabilityChange]);

  return null;
};

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  anchorEl,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth0();
  const { user: reduxUser } = useUserStore();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [pendingRejectNotification, setPendingRejectNotification] = useState<Notification | null>(null);
  const [showKycDialog, setShowKycDialog] = useState(false);
  const [acceptingOrderCode, setAcceptingOrderCode] = useState<string | null>(null);
  // Tracks, per notification, whether its shipment weight has loaded — used to block
  // actions on a traveler-offer notification (sender viewing a traveler's offer) until
  // the weight the price/earning calculation depends on is available.
  const [weightAvailabilityByNotification, setWeightAvailabilityByNotification] = useState<Record<string, boolean>>({});
  const handleWeightAvailabilityChange = React.useCallback((notificationId: string, isWeightAvailable: boolean) => {
    setWeightAvailabilityByNotification((prev) =>
      prev[notificationId] === isWeightAvailable ? prev : { ...prev, [notificationId]: isWeightAvailable }
    );
  }, []);
  const { isFullyVerified } = useKycVerification();
  const navigate = useNavigate();
  const { t } = useTranslation();


  // Use Zustand store for notification data
  const { openDetailsDialog } = useNotificationStore();

  // Determine user role from the store
  const isTraveler = isTravellerRole(reduxUser?.user_role);
  const currentUserRole = reduxUser?.user_role;
  const currentUserId = reduxUser?.uuid || String(reduxUser?.user_id || '');
  // Fallback for notifications cached before uuid was populated
  const legacyUserId = reduxUser?.user_id !== undefined ? String(reduxUser.user_id) : '';

  // Filter notifications by current user's role and user ID
  const allNotifications = useSelector((state: RootState) => state.notifications.notifications);
  const notifications = React.useMemo(() => {
    if (!currentUserRole || !currentUserId) {
      return [];
    }

    const filtered = allNotifications.filter((notification) => {
      // Skip role_switch notifications
      if ((notification.type as string) === 'role_switch') {
        return false;
      }

      // Hide shipment_offer_request notifications that have been acted upon (accepted or rejected)
      if (notification.type === 'shipment_offer_request' && (notification.data?.statusFlag === 'done' || notification.data?.statusFlag === 'rejected')) {
        return false;
      }

      // Check userId - if notification has a userId, it must match the current user
      // (either the current uuid, or the legacy encrypted user_id for older cached notifications)
      const notifOwnerId = (notification as any).owner_user_id || notification.userId;
      if (notifOwnerId && notifOwnerId !== currentUserId && !(legacyUserId && notifOwnerId === legacyUserId)) {
        return false;
      }

      if (notification.type === 'kyc_verification' || notification.type === 'shipment_offer_request') {
        return true;
      }

      const notifOwnerRole = (notification as any).owner_role || notification.user_role;
      if (notifOwnerRole && notifOwnerRole !== currentUserRole) {
        return false;
      }

      // For shipment_offer_approved/rejected, data.type holds the intended recipient role
      // Normalize "traveler" (backend) to "traveller" (app enum) before comparing
      const offerResultTypes = [
        'shipment_offer_approved',
        'shipment_offer_rejected',
        'offer_request_approved',
        'offer_request_rejected',
      ];
      if (offerResultTypes.includes(notification.type)) {
        const intendedRole = normalizeRole(notification.data?.type as string | undefined);
        if (intendedRole && intendedRole !== currentUserRole) {
          return false;
        }
      }

      return true;
    }).slice(0, 30);
    return filtered;
  }, [allNotifications, currentUserRole, currentUserId, legacyUserId]);

  // API mutations
  const senderAcceptMutation = useAcceptOfferRequestMutation();
  const senderRejectMutation = useRejectOfferRequestMutation();
  const travellerAcceptMutation = useTravellerAcceptOfferRequestMutation();
  const travellerRejectMutation = useTravellerRejectOfferRequestMutation();
  const markNotificationAsReadMutation = useMarkNotificationAsRead();

  useEffect(() => {
    // Load notifications when component mounts
    dispatch(loadNotifications());

    // Listen for localStorage changes
    const handleStorageChange = () => {
      dispatch(loadNotifications());
    };

    window.addEventListener('notification-state-changed', handleStorageChange);

    return () => {
      window.removeEventListener('notification-state-changed', handleStorageChange);
    };
  }, [dispatch]);

  useEffect(() => {
    if (anchorEl && isOpen) {
      const rect = anchorEl.getBoundingClientRect();
      const dropdownWidth = Math.min(643, window.innerWidth - 32);
      const dropdownMaxHeight = Math.min(500, window.innerHeight - rect.bottom - 24); // 24px padding from bottom

      // Position dropdown: align right edge of dropdown with right edge of bell icon
      const rightPosition = window.innerWidth - rect.right;

      // Ensure dropdown doesn't go off-screen on the left
      const minRight = window.innerWidth - dropdownWidth - 16; // 16px padding from left edge

      setDropdownPosition({
        top: rect.bottom + 8,
        right: Math.min(rightPosition, minRight),
      });
    }
  }, [anchorEl, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.notification-dropdown')
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, anchorEl, onClose]);

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!isActionableNotification(notification) && !notification.isRead) {
      dispatch(markAsRead(notification.id));
      if (notification.data?.notification_id) {
        markNotificationAsReadMutation.mutate(notification.data.notification_id);
      }
    }

    // Handle chat message navigation
    if ((notification.type === 'chat_message' || notification.type === 'text' || notification.type === 'message') && notification.data?.chat_room_id) {
      const chatRoomId = notification.data.chat_room_id;
      const orderCode = notification.data?.order_code || notification.data?.order_id || '';

      navigate(`/chat/${chatRoomId}`, {
        state: {
          from: 'order',
          orderCode: orderCode,
          userName: notification.data?.sender_name || '',
          userAvatar: notification.data?.sender_profile_image || '',
        },
      });

      onClose();
      return;
    }

    // Handle chat room created navigation
    if (notification.type === 'chat_room_created') {
      const chatRoomId = notification.data?.chat_room_id
        ?? notification.relatedEntityId?.replace('chat_', '');
      if (chatRoomId) {
        const orderCode = notification.data?.order_code || notification.data?.order_id || '';
        navigate(`/chat/${chatRoomId}`, {
          state: {
            from: 'order',
            orderCode: orderCode,
            userName: notification.data?.sender_name || (notification.data?.sender as any)?.full_name || '',
            userAvatar: notification.data?.sender_profile_image || (notification.data?.sender as any)?.profile_image || '',
          },
        });
        onClose();
        return;
      }
    }

    // Handle order group chat navigation
    if (notification.type === 'order_group_chat' && notification.data?.chat_room_id) {
      navigate(`/group-chat/${notification.data.chat_room_id}`, {
        state: {
          from: 'order',
          orderId: notification.data?.order_code || '',
        },
      });
      onClose();
      return;
    }

    // Handle order navigation
    const orderNavigationTypes = ['offer_request_approved', 'shipment_offer_approved', 'dropoff_reminder', 'pickup_reminder', 'sender_drop_off_confirmed', 'traveler_pickup_confirmed', 'traveler_delivery_checkout_confirmed', 'receiver_checkout_confirmed', 'order_under_review'];
    if (orderNavigationTypes.includes(notification.type) && notification.data?.order_code) {
      const route = isTraveler
        ? `/traveller/order-details/${notification.data.order_code}`
        : `/order-details/${notification.data.order_code}`;
      navigate(route);
      onClose();
      return;
    }
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());

    // Mark all unread notifications as read on backend
    notifications
      .filter((n) => !n.isRead && n.data?.notification_id)
      .forEach((n) => {
        markNotificationAsReadMutation.mutate(n.data.notification_id);
      });
  };

  const formatTimestamp = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  // Determine if notification is actionable
  const isActionableNotification = (notification: Notification): boolean => {
    // ONLY shipment_offer_request should show action buttons
    // approved and rejected are just confirmations - no actions needed
    const actionableTypes = [
      'shipment_offer_request',
    ];

    // Only show actions if statusFlag is still pending (regardless of read state)
    return (
      actionableTypes.includes(notification.type) &&
      notification.data?.statusFlag === 'pending'
    );
  };

  // Helper to mark notification as done in localStorage (statusFlag + isRead)
  const markNotificationDoneInStorage = (notificationId: string, statusFlag: 'done' | 'rejected') => {
    const notifications = notificationStorage.getNotifications();
    const notif = notifications.find((n) => n.id === notificationId);
    if (notif) {
      notif.isRead = true;
      notif.data = { ...notif.data, statusFlag };
      const updated = notifications.map((n) => (n.id === notificationId ? notif : n));
      localStorage.setItem('skyporter_notifications', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('notification-state-changed'));
    }
  };

  // Handle accept (tick button)
  const handleAccept = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isFullyVerified) {
      setShowKycDialog(true);
      return;
    }

    const orderCode = notification.data?.order_code;

    if (!orderCode) {
      return;
    }

    setAcceptingOrderCode(String(orderCode));
    try {
      // Use actual user role from store instead of inferring from notification type
      if (isTraveler) {
        await travellerAcceptMutation.mutateAsync({ order_code: String(orderCode) });
        markNotificationDoneInStorage(notification.id, 'done');
        dispatch(markAsRead(notification.id));
        if (notification.data?.notification_id) {
          markNotificationAsReadMutation.mutate(notification.data.notification_id);
        }
        onClose();
        navigate(`/traveller/order-details/${orderCode}`);
        return;
      } else {
        const response = await senderAcceptMutation.mutateAsync({
          order_code: String(orderCode),
          success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/payment/cancel`,
        });

        // Redirect sender to Stripe payment URL — mark notification done only after Stripe success
        if (response?.stripe_session?.url) {
          localStorage.setItem('pending_notification_id', notification.id);
          localStorage.setItem('pending_offer_order_code', String(orderCode));
          if (notification.data?.notification_id) {
            localStorage.setItem('pending_backend_notification_id', String(notification.data.notification_id));
          }
          window.location.href = response.stripe_session.url;
          return;
        }
      }

      markNotificationDoneInStorage(notification.id, 'done');
      dispatch(markAsRead(notification.id));
      if (notification.data?.notification_id) {
        markNotificationAsReadMutation.mutate(notification.data.notification_id);
      }
      onClose();
      navigate(`/order-details/${orderCode}`);
    } catch {
      // Error toast is already shown by the API interceptor for 4xx/5xx errors
    } finally {
      setAcceptingOrderCode(null);
    }
  };

  // Handle reject (cross button) - closes dropdown and opens DeclineOfferDialog to capture reason
  const handleReject = (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isFullyVerified) {
      setShowKycDialog(true);
      return;
    }

    const orderCode = notification.data?.order_code;
    if (!orderCode) return;
    setPendingRejectNotification(notification);
    setShowDeclineDialog(true);
    onClose();
  };

  // Called when user submits the decline reason
  const handleDeclineSubmit = async (reason: string) => {
    if (!pendingRejectNotification) return;

    try {
      const orderCode = pendingRejectNotification.data?.order_code;
      if (!orderCode) return;

      if (isTraveler) {
        await travellerRejectMutation.mutateAsync({ order_code: String(orderCode), reason });
      } else {
        await senderRejectMutation.mutateAsync({ order_code: String(orderCode), reason });
      }

      markNotificationDoneInStorage(pendingRejectNotification.id, 'rejected');
      dispatch(markAsRead(pendingRejectNotification.id));
      if (pendingRejectNotification.data?.notification_id) {
        markNotificationAsReadMutation.mutate(pendingRejectNotification.data.notification_id);
      }

      setShowDeclineDialog(false);
      setPendingRejectNotification(null);
    } catch (error) {
      // Don't mark as rejected if API call failed
    }
  };

  // Handle view details - use Zustand store instead of local state
  const handleViewDetails = (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    // Open dialog using Zustand store - this will be rendered outside the dropdown
    openDetailsDialog(notification);
  };

  return (
    <>
      <KycVerificationDialog
        isOpen={showKycDialog}
        onClose={() => setShowKycDialog(false)}
      />
      <DeclineOfferDialog
        open={showDeclineDialog}
        onClose={() => {
          setShowDeclineDialog(false);
          setPendingRejectNotification(null);
        }}
        onSubmit={handleDeclineSubmit}
        isLoading={travellerRejectMutation.isPending || senderRejectMutation.isPending}
        userType={isTraveler ? 'traveler' : 'sender'}
        notificationId={pendingRejectNotification?.data?.notification_id as string | number | undefined}
      />

      {!isOpen ? null : <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 bg-transparent z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Dropdown */}
      <div
        className="notification-dropdown fixed z-50 bg-white border border-[#E9EAEB] rounded-lg shadow-lg flex flex-col"
        style={{
          top: dropdownPosition.top,
          right: dropdownPosition.right,
          width: 'min(643px, calc(100vw - 2rem))',
          maxHeight: `calc(100vh - ${dropdownPosition.top}px - 16px)`,
          boxShadow:
            '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#E9EAEB] flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#181D27]">{t('notifications.title')}</h3>
          {notifications.some((n) => !n.isRead) && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs font-medium text-[#9B1B5A] hover:text-[#7A0056] transition-colors"
            >
              {t('notifications.markAllAsRead')}
            </button>
          )}
        </div>

        {/* Notification List */}
        <div className="overflow-y-auto flex-1">
          <div className="flex flex-col gap-2 p-3">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-sm text-[#667071]">{t('notifications.noNotifications')}</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const showActions = isActionableNotification(notification);
                // Sender viewing a traveler's offer: block actions until the shipment weight
                // the price calculation depends on has loaded (mirrors SenderDetailsDialog).
                const isTravelerOfferMissingWeight = !isTraveler && weightAvailabilityByNotification[notification.id] === false;
                const isOfferNotification = notification.type === 'shipment_offer_request' ||
                                          notification.type === 'offer_request_approved' ||
                                          notification.type === 'offer_request_rejected';
                const isChatNotification = notification.type === 'chat_message' || notification.type === 'text' || notification.type === 'message' || notification.type === 'order_group_chat';
                const isOrderNavigation = (notification.type === 'offer_request_approved' || notification.type === 'shipment_offer_approved') && !!notification.data?.order_code;
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`
                      relative flex flex-row items-start p-4 sm:p-6 gap-3 rounded-xl border transition-all
                      ${
                        !notification.isRead
                          ? 'bg-[#FCF5F9] border-[#E5E7EB]'
                          : 'bg-white border-[#F3F3F5] hover:bg-gray-50'
                      }
                      ${showActions ? 'cursor-default' : 'cursor-pointer'}
                    `}
                  >
                    {/* Unread Indicator */}
                    {!notification.isRead && (
                      <div className="absolute top-4 right-4 w-2 h-2 bg-[#7A0056] rounded-full" />
                    )}

                    {/* Box Icon for Offer Notifications */}
                    {isOfferNotification && (
                      <div className="flex-shrink-0 mt-1">
                        <img src={boxIcon} alt="" width={32} height={32} />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 flex flex-col gap-3">
                      {/* Chat Message Notification */}
                      {isChatNotification ? (
                        <>
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 pr-4">
                              <p className="text-sm sm:text-base font-semibold text-[#181D27] leading-6">
                                {t('notification.chatMessage.newMessage')}
                              </p>
                              {notification.type === 'order_group_chat' ? (
                                <p className="text-xs text-[#667071] mt-1">
                                  {t('notification.orderGroupChat.inDeliveryChat')}
                                </p>
                              ) : notification.data?.sender_name && (
                                <p className="text-xs text-[#667071] mt-1">
                                  {t('chat.from')} {notification.data.sender_name}
                                </p>
                              )}
                            </div>
                            <span className="text-xs font-bold text-[#475569] whitespace-nowrap">
                              {formatTimestamp(notification.timestamp)}
                            </span>
                          </div>
                          <button
                            className="text-sm font-medium text-[#9B1B5A] hover:text-[#7A0056] transition-colors text-left w-fit underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {t('notification.chatMessage.viewChat')}
                          </button>
                        </>
                      ) : (
                        /* Regular Notification */
                        <>
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 pr-4">
                              <p
                                className="text-sm sm:text-base font-bold leading-6"
                                style={{ color: isTraveler ? colors.traveller.primary : colors.sender.primary }}
                              >
                                {getNotificationTitle(notification, isTraveler ? 'traveller' : 'sender')}
                              </p>
                              <p
                                className="text-sm sm:text-base font-normal text-[#515D6F] leading-6 whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{
                                  __html: (() => {
                                    const base = getNotificationMessage(notification, isTraveler ? 'traveller' : 'sender');
                                    const suffix = notification.data?.hasCategoryMismatch
                                      ? ` ${CATEGORY_MISMATCH_WARNING}`
                                      : '';
                                    return (base + suffix)
                                      .replace(/\*\*("?[^*]+"?)\*\*/g, '<strong style="font-weight:600;color:#181D27">$1</strong>')
                                      .replace(/\n/g, '<br/>');
                                  })()
                                }}
                              />
                              {notification.type === 'shipment_offer_request' &&
                                notification.data?.shipment_id &&
                                notification.data?.itinerary_id && (
                                  <ShipmentOfferMismatchWarning
                                    shipmentId={String(notification.data.shipment_id)}
                                    itineraryId={String(notification.data.itinerary_id)}
                                    notificationId={notification.id}
                                    onWeightAvailabilityChange={!isTraveler ? handleWeightAvailabilityChange : undefined}
                                  />
                                )}
                            </div>
                            <span className="text-xs font-bold text-[#475569] whitespace-nowrap">
                              {formatTimestamp(notification.timestamp)}
                            </span>
                          </div>
                          {notification.type === 'chat_room_created' && (
                            <button
                              className="text-sm font-medium text-[#9B1B5A] hover:text-[#7A0056] transition-colors text-left w-fit underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                            >
                              {t('notification.chatMessage.viewChat')}
                            </button>
                          )}
                          {isOrderNavigation && !showActions && (
                            <button
                              className="text-sm font-medium text-[#9B1B5A] hover:text-[#7A0056] transition-colors text-left w-fit underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                            >
                              {t('notification.viewOrder')}
                            </button>
                          )}
                          {(notification.type === 'offer_request_rejected' || notification.type === 'shipment_offer_rejected') && !showActions && (
                            <button
                              className="text-sm font-medium text-[#9B1B5A] hover:text-[#7A0056] transition-colors text-left w-fit underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/');
                                onClose();
                              }}
                            >
                              {isTraveler
                                ? t('offerNotifications.common.viewSenders')
                                : t('offerNotifications.common.viewTravelers')}
                            </button>
                          )}
                        </>
                      )}

                      {/* Action Buttons for Actionable Notifications */}
                      {showActions && (() => {
                        const isAcceptingThis = acceptingOrderCode === String(notification.data?.order_code || '');
                        const isDisabled = isAcceptingThis || isTravelerOfferMissingWeight;
                        return (
                        <div className="flex flex-row items-center gap-1.5 sm:gap-2 mt-2">
                          {/* View Details Button */}
                          <button
                            onClick={(e) => handleViewDetails(notification, e)}
                            disabled={isDisabled}
                            style={{
                              backgroundColor: colors.traveller.primary,
                              color: colors.neutral.white,
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                            className="px-3 py-1.5 text-xs sm:px-6 sm:py-2.5 sm:text-sm font-semibold rounded-lg hover:opacity-90 transition-all whitespace-nowrap"
                          >
                            View Details
                          </button>

                          {/* Reject Button (X) */}
                          <button
                            onClick={(e) => handleReject(notification, e)}
                            disabled={isDisabled}
                            style={{
                              backgroundColor: colors.action.reject,
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                            className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg hover:opacity-90 transition-all flex-shrink-0"
                            title={t('notifications.reject')}
                          >
                            <X size={16} className="sm:hidden" color={colors.neutral.white} />
                            <X size={20} className="hidden sm:block" color={colors.neutral.white} />
                          </button>

                          {/* Accept Button (✓) */}
                          <button
                            onClick={(e) => handleAccept(notification, e)}
                            disabled={isDisabled}
                            style={{
                              backgroundColor: colors.action.accept,
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                            className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg hover:opacity-90 transition-all flex-shrink-0"
                            title={t('notifications.accept')}
                          >
                            <Check size={16} className="sm:hidden" color={colors.neutral.white} />
                            <Check size={20} className="hidden sm:block" color={colors.neutral.white} />
                          </button>
                        </div>
                        );
                      })()}

                      {/* Action Link (for non-actionable notifications) */}
                      {notification.data?.actionLink && !showActions && (
                        <a
                          href={notification.data.actionLink}
                          className="text-sm sm:text-base font-normal text-[#9B1B5A] underline hover:text-[#7A0056] transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {notification.data.actionText || 'View Details'}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      </>}
    </>
  );
};
